// Minimal toast system — replaces window.alert(). Single bottom-right toast
// stack; auto-dismisses after 4s; click to dismiss early. Used through a
// hook (useToast) plus a global mount point (<ToastHost />) rendered once at
// the App root.

import { create } from 'zustand';
import { cn } from '@/lib/cn';

export type ToastTone = 'success' | 'error' | 'info';
export interface Toast { id: string; tone: ToastTone; title: string; body?: string }

interface ToastState {
  toasts: Toast[];
  push: (t: Omit<Toast, 'id'>) => string;
  dismiss: (id: string) => void;
}

const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (t) => {
    const id = `toast-${Math.random().toString(36).slice(2, 9)}`;
    set((s) => ({ toasts: [...s.toasts, { id, ...t }] }));
    setTimeout(() => set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) })), 4000);
    return id;
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) })),
}));

export function useToast() {
  const push = useToastStore((s) => s.push);
  return {
    success: (title: string, body?: string) => push({ tone: 'success', title, body }),
    error:   (title: string, body?: string) => push({ tone: 'error', title, body }),
    info:    (title: string, body?: string) => push({ tone: 'info', title, body }),
  };
}

export function ToastHost() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);
  if (toasts.length === 0) return null;
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm" role="region" aria-label="Notifications">
      {toasts.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => dismiss(t.id)}
          className={cn(
            'rounded-md border bg-frost p-3 text-left shadow-card transition hover:shadow-card-hover',
            t.tone === 'success' ? 'border-emerald/40' :
            t.tone === 'error'   ? 'border-crimson/40' :
                                   'border-cobalt/40',
          )}
        >
          <div className={cn('text-body-sm font-medium',
            t.tone === 'success' ? 'text-emerald' :
            t.tone === 'error'   ? 'text-crimson' :
                                   'text-cobalt')}>
            {t.title}
          </div>
          {t.body && <div className="mt-0.5 text-caption text-slate">{t.body}</div>}
        </button>
      ))}
    </div>
  );
}
